import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { UserProfile, Version } from '../types/common';
import { loadFromLocalStorage, saveToLocalStorage, debounce } from '../utils/helpers';
import { STORAGE_KEYS, DEFAULT_IDS } from '../constants';
import { migrateAllProfileIds } from '../utils/profileManager';

// Define the shape of our context
interface AppContextType {
  // User profile
  userProfile: UserProfile;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;

  // Versions
  versions: Version[];
  selectedVersion: string;
  setVersions: React.Dispatch<React.SetStateAction<Version[]>>;
  setSelectedVersion: React.Dispatch<React.SetStateAction<string>>;

  // UI state
  expandedSections: Record<string, boolean>;
  setExpandedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

  // Theme
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the context with a default undefined value
const AppContext = createContext<AppContextType | undefined>(undefined);

// Define props for our provider component
interface AppProviderProps {
  children: ReactNode;
}

// Create the provider component
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // User profile state
  const [userProfile, setUserProfile] = useState<UserProfile>(() => {
    const storedProfile = loadFromLocalStorage(STORAGE_KEYS.USER_PROFILE, {
      memberId: DEFAULT_IDS.MEMBER_ID,
      accountId: DEFAULT_IDS.ACCOUNT_ID,
      username: '',
      email: '',
      fullName: '',
      phone: '',
      company: '',
      linkedin: ''
    });

    // Always ensure sensitive data is anonymized
    return {
      ...storedProfile,
      memberId: DEFAULT_IDS.MEMBER_ID, // Use anonymized member ID
      accountId: DEFAULT_IDS.ACCOUNT_ID, // Use anonymized account ID
      email: '' // Don't expose email
    };
  });

  // Version control state
  const [versions, setVersions] = useState<Version[]>(() =>
    loadFromLocalStorage(STORAGE_KEYS.VERSIONS, [])
  );
  const [selectedVersion, setSelectedVersion] = useState<string>(() =>
    loadFromLocalStorage(STORAGE_KEYS.SELECTED_VERSION, 'current')
  );

  // UI state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    loadFromLocalStorage(STORAGE_KEYS.EXPANDED_SECTIONS, {})
  );

  // Theme state
  const [darkMode, setDarkMode] = useState<boolean>(() =>
    loadFromLocalStorage(STORAGE_KEYS.THEME, false)
  );

  // Create debounced save function
  const debouncedSaveUserProfile = useCallback(
    (data: UserProfile) => {
      const debouncedFn = debounce((profileData: UserProfile) => {
        saveToLocalStorage(STORAGE_KEYS.USER_PROFILE, profileData);
      }, 500);
      debouncedFn(data);
    },
    []
  );

  // Save state to localStorage whenever it changes
  useEffect(() => {
    debouncedSaveUserProfile(userProfile);
  }, [userProfile, debouncedSaveUserProfile]);

  // Migrate all profiles to ensure they have 9-digit account IDs
  useEffect(() => {
    // Run once on app initialization
    console.log('Checking and migrating profiles to 9-digit account and member IDs if needed...');
    migrateAllProfileIds();

    // Also check the current user profile
    if (userProfile && userProfile.accountId) {
      let needsUpdate = false;
      let updatedUserProfile = {...userProfile};

      // Check if the current user account ID is valid (9 digits)
      const isValidAccountId = /^[1-9]\d{8}$/.test(userProfile.accountId);

      if (!isValidAccountId) {
        console.log(`Current user profile needs account ID migration: ${userProfile.accountId}`);

        // Generate a 9-digit account ID
        const firstDigit = Math.floor(Math.random() * 9) + 1;
        const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
        const newAccountId = `${firstDigit}${remainingDigits}`;

        updatedUserProfile.accountId = newAccountId;
        needsUpdate = true;

        console.log(`Migrated current user account ID: ${userProfile.accountId} → ${newAccountId}`);
      }

      // Check if the current user member ID is valid (9 digits)
      const isValidMemberId = /^[1-9]\d{8}$/.test(userProfile.memberId);

      if (!isValidMemberId) {
        console.log(`Current user profile needs member ID migration: ${userProfile.memberId}`);

        // Generate a 9-digit member ID
        const firstDigit = Math.floor(Math.random() * 9) + 1;
        const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
        const newMemberId = `${firstDigit}${remainingDigits}`;

        updatedUserProfile.memberId = newMemberId;
        needsUpdate = true;

        console.log(`Migrated current user member ID: ${userProfile.memberId} → ${newMemberId}`);
      }

      // Update the user profile if needed
      if (needsUpdate) {
        setUserProfile(updatedUserProfile);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.VERSIONS, versions);
  }, [versions]);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.SELECTED_VERSION, selectedVersion);
  }, [selectedVersion]);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.EXPANDED_SECTIONS, expandedSections);
  }, [expandedSections]);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.THEME, darkMode);
  }, [darkMode]);

  // Create the context value object
  const contextValue = {
    userProfile,
    setUserProfile,
    versions,
    setVersions,
    selectedVersion,
    setSelectedVersion,
    expandedSections,
    setExpandedSections,
    darkMode,
    setDarkMode
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Create a hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
